
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

export interface ExperienceData {
  currentLevel: number;
  currentRank: string;
  totalXp: number;
  nextLevelXp: number;
  progressPercentage: number;
}

const calculateLevel = (xp: number): number => {
  if (xp < 100) return 1;
  if (xp < 300) return 2;
  if (xp < 600) return 3;
  if (xp < 1000) return 4;
  if (xp < 1500) return 5;
  if (xp < 2100) return 6;
  if (xp < 2800) return 7;
  if (xp < 3600) return 8;
  if (xp < 4500) return 9;
  if (xp < 5500) return 10;
  if (xp < 6600) return 11;
  if (xp < 7800) return 12;
  if (xp < 9100) return 13;
  if (xp < 10500) return 14;
  if (xp < 12000) return 15;
  if (xp < 13600) return 16;
  if (xp < 15300) return 17;
  if (xp < 17100) return 18;
  if (xp < 19000) return 19;
  return 20;
};

const calculateNextLevelXp = (level: number): number => {
  const xpThresholds = [0, 100, 300, 600, 1000, 1500, 2100, 2800, 3600, 4500, 5500, 6600, 7800, 9100, 10500, 12000, 13600, 15300, 17100, 19000, 21000];
  return xpThresholds[level] || 21000;
};

const getRankFromLevel = (level: number): string => {
  if (level <= 2) return 'Neophyte';
  if (level <= 4) return 'Acolyte';
  if (level <= 6) return 'Seeker';
  if (level <= 8) return 'Initiate';
  if (level <= 10) return 'Adept';
  if (level <= 12) return 'Keeper';
  if (level <= 14) return 'Architect';
  if (level <= 16) return 'Ascendant';
  if (level <= 18) return 'Harbinger';
  return 'Master';
};

export const useExperience = () => {
  const { user } = useAuth();
  
  const { data: experience, isLoading, error } = useQuery({
    queryKey: ['user-experience', user?.id],
    queryFn: async (): Promise<ExperienceData | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_experience')
        .select('total_xp')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user experience:', error);
        return null;
      }

      const totalXp = data?.total_xp || 0;
      const currentLevel = calculateLevel(totalXp);
      const currentRank = getRankFromLevel(currentLevel);
      const nextLevelXp = calculateNextLevelXp(currentLevel);
      const currentLevelXp = calculateNextLevelXp(currentLevel - 1);
      const progressPercentage = currentLevel >= 20 ? 100 : ((totalXp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100;

      return {
        currentLevel,
        currentRank,
        totalXp,
        nextLevelXp,
        progressPercentage: Math.max(0, Math.min(100, progressPercentage))
      };
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes - reduced refetching
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });

  return {
    experience,
    isLoading,
    error
  };
};
